import React from "react";
import {
  DiagnosisNutritionSubsection,
  DiagnosisNutritionSubsectionStack,
  PrintDiagnosisNutritionSubsection,
  PrintDiagnosisNutritionSubsectionStack,
} from "./DiagnosisNutritionSubsection";
import {
  NutritionDietViewFields,
  NutritionRestrictionViewFields,
} from "./NutritionSectionFields";
import { AllergyChips } from "./MultiRowFields";
import {
  buildDiagnosisAllergies,
  buildAllergies,
} from "../patientForm.shared";
import {
  buildNutrition,
  hasDietData,
  hasRestrictionData,
} from "../patientNutrition.shared";

const ViewField = ({ label, value, className = "" }) => (
  <div className={`min-w-0 py-0.5 ${className}`}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-0.5 text-sm text-slate-900 font-medium break-words leading-snug">
      {value || "—"}
    </div>
  </div>
);

const DiagnosisViewRows = ({ rows = [] }) => (
  <div className="space-y-3">
    {rows.map((row, idx) => (
      <div
        key={`dx-${idx}`}
        className={`grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2 ${
          idx > 0 ? "pt-3 border-t border-slate-100" : ""
        }`}
      >
        <ViewField
          label={idx === 0 ? "Primary Diagnosis" : "Secondary Diagnosis"}
          value={
            row.diagnosisCode &&
            row.primaryDiagnosis &&
            !String(row.primaryDiagnosis).startsWith(row.diagnosisCode)
              ? `${row.diagnosisCode} - ${row.primaryDiagnosis}`
              : row.primaryDiagnosis
          }
        />
        <ViewField label="Clinical Group" value={row.clinicalGroup} />
        <div className="space-y-0.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Billable
          </p>
          <div className="inline-flex items-center gap-2 h-[34px]">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-indigo-500"
              checked={row.isBillable === true}
              readOnly
              disabled
            />
            <span
              className={`text-xs font-medium whitespace-nowrap ${
                row.isBillable === true ? "text-indigo-700" : "text-slate-500"
              }`}
            >
              {row.isBillable === true ? "Billable" : "Not billable"}
            </span>
          </div>
        </div>
        {idx === 0 && row.clinicalGroup === "No Group Assigned" ? (
          <p className="sm:col-span-3 text-[11px] text-amber-700">
            No PDGM clinical group assigned for this code
          </p>
        ) : null}
      </div>
    ))}
  </div>
);

const PrintDiagnosisRows = ({ rows = [] }) => (
  <div className="space-y-2">
    {rows.map((row, idx) => {
      const label = idx === 0 ? "Primary Diagnosis" : "Secondary Diagnosis";
      const diagnosisText =
        row.diagnosisCode &&
        row.primaryDiagnosis &&
        !String(row.primaryDiagnosis).startsWith(row.diagnosisCode)
          ? `${row.diagnosisCode} - ${row.primaryDiagnosis}`
          : row.primaryDiagnosis || row.diagnosisCode;
      return (
        <div
          key={idx}
          className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 border-b border-slate-100 last:border-0 pb-2 last:pb-0"
        >
          <PrintField label={label} value={diagnosisText} />
          <PrintField label="Clinical Group" value={row.clinicalGroup} />
          <PrintField label="Billable" value={row.isBillable ? "Yes" : "No"} />
        </div>
      );
    })}
  </div>
);

const PrintField = ({ label, value }) => (
  <div className="min-w-0 py-0.5 break-inside-avoid">
    <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-0.5 text-[12px] text-slate-900 font-medium break-words leading-snug">
      {value == null || String(value).trim() === "" ? "—" : value}
    </div>
  </div>
);

/** View mode — four clearly labeled subsections. */
export const DiagnosisNutritionViewContent = ({ patient }) => {
  const diagnoses = buildDiagnosisAllergies(patient);
  const allergies = buildAllergies(patient);
  const nutrition = buildNutrition(patient);

  return (
    <DiagnosisNutritionSubsectionStack>
      <DiagnosisNutritionSubsection
        title="Diagnosis"
        empty={!diagnoses.length}
        emptyText="No diagnoses added"
      >
        <DiagnosisViewRows rows={diagnoses} />
      </DiagnosisNutritionSubsection>

      <DiagnosisNutritionSubsection
        title="Allergies"
        empty={!allergies.length}
        emptyText="No allergies added"
      >
        <AllergyChips allergies={allergies} />
      </DiagnosisNutritionSubsection>

      <DiagnosisNutritionSubsection
        title="Diet"
        empty={!hasDietData(nutrition)}
        emptyText="No diet information added"
      >
        <NutritionDietViewFields patient={patient} bare />
      </DiagnosisNutritionSubsection>

      <DiagnosisNutritionSubsection
        title="Restriction"
        empty={!hasRestrictionData(nutrition)}
        emptyText="No restriction information added"
      >
        <NutritionRestrictionViewFields patient={patient} bare />
      </DiagnosisNutritionSubsection>
    </DiagnosisNutritionSubsectionStack>
  );
};

/** Print layout — four subsections. */
export const DiagnosisNutritionPrintContent = ({
  diagnoses = [],
  allergies = [],
  allergyName = "",
  nutrition,
  FieldGrid,
  PrintField: PrintFieldProp,
  formatDietType,
  formatSupplementEnteral,
  formatSodiumRestriction,
  formatCarbohydrateRestriction,
}) => {
  const PF = PrintFieldProp || PrintField;

  return (
    <PrintDiagnosisNutritionSubsectionStack>
      <PrintDiagnosisNutritionSubsection
        title="Diagnosis"
        empty={!diagnoses.length}
        emptyText="No diagnoses added"
      >
        {diagnoses.length ? <PrintDiagnosisRows rows={diagnoses} /> : null}
      </PrintDiagnosisNutritionSubsection>

      <PrintDiagnosisNutritionSubsection
        title="Allergies"
        empty={!allergies.length}
        emptyText="No allergies added"
      >
        <PF label="Allergy List" value={allergyName} />
      </PrintDiagnosisNutritionSubsection>

      <PrintDiagnosisNutritionSubsection
        title="Diet"
        empty={!hasDietData(nutrition)}
        emptyText="No diet information added"
      >
        <FieldGrid>
          <PF label="Diet Type" value={formatDietType(nutrition)} />
          <PF label="Food Texture" value={nutrition.foodTexture} />
          <PF label="Fluid Management" value={nutrition.fluidManagement} />
          <PF label="Meal Assistance" value={nutrition.mealAssistance} />
          <PF
            label="Supplement / Enteral"
            value={formatSupplementEnteral(nutrition)}
          />
          <PF
            label="Diet Instructions"
            value={nutrition.dietInstructions}
            className="sm:col-span-2 md:col-span-3"
          />
        </FieldGrid>
      </PrintDiagnosisNutritionSubsection>

      <PrintDiagnosisNutritionSubsection
        title="Restriction"
        empty={!hasRestrictionData(nutrition)}
        emptyText="No restriction information added"
      >
        <FieldGrid>
          <PF
            label="Sodium Restriction"
            value={formatSodiumRestriction(nutrition)}
          />
          <PF label="Fluid Restriction" value={nutrition.fluidRestriction} />
          <PF
            label="Carbohydrate / Sugar"
            value={formatCarbohydrateRestriction(nutrition)}
          />
          <PF
            label="Foods to Avoid"
            value={nutrition.restrictedFoods}
            className="sm:col-span-2 md:col-span-3"
          />
          <PF
            label="Texture / Consistency Restrictions"
            value={nutrition.textureRestrictions}
            className="sm:col-span-2 md:col-span-3"
          />
          <PF
            label="Restriction Notes"
            value={nutrition.restrictionNotes}
            className="sm:col-span-2 md:col-span-3"
          />
        </FieldGrid>
      </PrintDiagnosisNutritionSubsection>
    </PrintDiagnosisNutritionSubsectionStack>
  );
};
